// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// A fingertip through a face (M13-05).
//
// Build mode's tiles, rows and buttons are *tapped*, never pinched and never pointed at. The
// reasons are the product's own, recorded before this file existed: a relaxed hand reads as
// pinched (`MachineInteraction`, five of five heartbeats), so a pinch is not a decision; and
// nothing in this product aims a ray, so a tap is the one gesture every hand already makes at
// every control on the cabinet.
//
// This is `PressGate` for a flat face instead of a capsule. The same two ideas carry over:
//
// **Hysteresis, not a threshold.** A tracked fingertip held at a surface jitters by about a
// millimetre. Pressing at one depth and releasing at another leaves a dead band wider than the
// jitter, so a finger resting on a tile does not machine-gun it.
//
// **The edge, not the state.** A tap is a moment — the click, the flash, the row lighting — and
// the caller wants it once. `pressedEdge` fires on the frame the face is entered and never
// again while the finger stays.
//
// And one idea that is this file's own: **the volume behind the face is deeper than the volume
// in front.** A virtual face has nothing to push against, so nothing tells a hand to stop; a
// finger that carries on through the glass is still pressing what it pressed, until it has gone
// so far that it plainly means something else. The same lesson cost a quarter of all flipper
// presses when the buttons were spheres (M8-28).
//
// **A press is a push, measured from the shallowest point the finger reached over the face.**
// The first gate pressed only a fingertip whose previous sample was clear in front of the face —
// "approach through the front, or it is not a tap" — so that a hand which appeared already inside
// the volume, or slid in sideways from below, pressed nothing. A finger that reaches a floating
// panel from the side is already within a finger's width of the picture when it arrives over a
// row, and under that rule pushing in from there never crossed the threshold: the person saw the
// row light, pushed, and nothing happened until they pulled back and came again from the front.
// The 23:14 session on 13 Sep 2026 has eight rows lit and left without a press in its busiest
// fifteen seconds (beside eight that pressed), and the owner's "the left-palm command was
// ignored" is that experience from the inside. (First read as fourteen highlights and no press,
// because the console did not print page presses; corrected the same night.) So the gate now
// keeps the shallowest point the finger has reached since it came over the face — the
// sample before arrival counts, so a poke from the front presses on the frame it always did — and
// a finger that has travelled `TOUCH_TRAVEL` in from that point, to within `TOUCH_IN` of the
// picture, has pressed. The shallowest point has to be on the person's side of the face: a hand
// behind the panel, where the other hand is, presses nothing however it moves, and a hand that
// appears already inside and rests there presses nothing until it actually pushes.
//
// Pure: a face is a width and a height, and the fingertip arrives in the face's own frame —
// the caller has done `mesh.worldToLocal` — with +z toward the person and the face at z = 0.
// Nothing here knows about Three, hands, or which face this is. One gate per face per hand.

/** A flat, rectangular face, metres. Its own frame: origin at the centre, +z toward the person. */
export interface TapFace {
  readonly width: number
  readonly height: number
}

/** A fingertip in a face's own frame, metres. */
export interface FaceLocal {
  readonly x: number
  readonly y: number
  readonly z: number
}

/**
 * How far in front of the face a fingertip has to come to press it.
 *
 * Twelve millimetres: a finger's own thickness, so a tip that touches the picture is pressing it,
 * and a tip hovering a finger-width off is not.
 */
export const TOUCH_IN = 0.012

/** Pulling back past this releases. The gap to `TOUCH_IN` is the anti-chatter, as `PressGate`'s. */
export const TOUCH_OUT = 0.03

/**
 * How far *through* the face a held press may go before it stops being one.
 *
 * Deeper than the front, deliberately — 35 mm, the wrist panel's own `TOUCH_DEPTH` — because a
 * hand pushing at nothing keeps going, and a press that released because the finger went two
 * centimetres through the picture would be a button that works only for a hand that stops dead.
 */
export const TOUCH_BEHIND = 0.035

/** A held press may drift this far past the face's edge before it releases. */
export const TOUCH_SLACK = 0.012

/**
 * How far a fingertip has to travel *into* the face, from the shallowest point it has reached
 * over it, to press.
 *
 * Ten millimetres: a push, not a tremor. A tracked fingertip jitters by about a millimetre, and
 * a hand at rest drifts slower than this over the seconds a person leaves it there. Less than
 * `TOUCH_IN`, so a poke that arrives from clear in front still presses at the depth it always
 * did; a finger already hovering within a finger's width presses a finger's width further in.
 */
export const TOUCH_TRAVEL = 0.01

/** What happened to one face, for one hand, this frame. */
export interface TapState {
  /** Is the finger pressing it? */
  readonly down: boolean
  /** Did it press *this frame*? The moment the click, the flash and the action fire on. */
  readonly pressedEdge: boolean
  /** Did it release this frame? */
  readonly releasedEdge: boolean
  /**
   * Where on the face the press landed, in the face's frame, metres from the centre.
   *
   * Captured at the press edge and held while the press lasts, so a row is decided by where the
   * finger arrived and not by where it wandered while pushing.
   */
  readonly at: readonly [number, number] | undefined
}

const IDLE: TapState = { down: false, pressedEdge: false, releasedEdge: false, at: undefined }

/** A surface switch cannot turn the consumed source poke into a destination press. */
export class NavigationPointerRelease {
  blocked = false
  private tip: FaceLocal = { x: 0, y: 0, z: 0 }
  private outward: FaceLocal = { x: 0, y: 0, z: 0 }
  private button = false
  private newFrame = false
  get buttonSelection(): boolean { return this.button }
  invalidateFrame(): void { if (this.blocked) this.newFrame = true }
  consume(tip: FaceLocal, outward: FaceLocal, button: boolean): void {
    this.tip = { ...tip }; this.outward = { ...outward }; this.button = button; this.newFrame = false; this.blocked = true
  }
  advance(present: boolean, buttonDown: boolean, tip: FaceLocal, freshOutward?: FaceLocal): boolean {
    if (!this.blocked) return true
    if (!present) return false
    if (this.newFrame && !this.button) {
      if (freshOutward === undefined) return false
      this.tip = { ...tip }; this.outward = { ...freshOutward }; this.newFrame = false
      return false
    }
    const travel = (tip.x - this.tip.x) * this.outward.x + (tip.y - this.tip.y) * this.outward.y + (tip.z - this.tip.z) * this.outward.z
    if (this.button ? !buttonDown : travel >= .03 && Number.isFinite(travel)) this.blocked = false
    return !this.blocked
  }
}

export class TapGate {
  private readonly face: TapFace
  private down = false
  private at: readonly [number, number] | undefined
  /** The previous sample, which counts as the shallowest point on arrival. `undefined` = no hand. */
  private previous: FaceLocal | undefined
  /**
   * The shallowest the fingertip has been since it came over the face, metres; `undefined` while
   * it is off the face, absent, or pressing. A push is measured down from here.
   */
  private shallow: number | undefined

  constructor(face: TapFace) {
    this.face = face
  }

  get pressed(): boolean {
    return this.down
  }

  /**
   * One fingertip sample in the face's frame, or `undefined` for a hand that is not there.
   *
   * Led by the caller, not here, for `PressGate`'s reason: the velocity lead is a property of
   * the input source — 40 ms for a hand, 4 ms for a controller — and a gate that led its own
   * input would need to know what kind of hand was pressing it.
   */
  advance(tip: FaceLocal | undefined, physicalPush = true): TapState {
    if (tip === undefined) {
      this.shallow = undefined
      return this.settle(false, undefined, undefined)
    }
    const previous = this.previous
    this.previous = tip

    if (this.down) {
      // Held: stay down inside a slightly larger face and a deeper band, release on leaving.
      const inside = this.within(tip, TOUCH_SLACK)
      const inBand = tip.z <= TOUCH_OUT && tip.z >= -TOUCH_BEHIND
      const still = inside && inBand
      if (!still) this.shallow = undefined
      return this.settle(still, tip, this.at)
    }

    // Not down. Off the face there is nothing to push on, and nothing to measure a push from.
    if (!this.within(tip, 0)) {
      this.shallow = undefined
      return this.settle(false, tip, undefined)
    }
    // Over it: the shallowest the finger has been since it arrived. The sample before arrival
    // counts, so a poke from clear in front presses on the frame it enters the band, as it
    // always did; a finger that arrived at the face's depth has to push in from there.
    const from =
      this.shallow === undefined
        ? previous === undefined
          ? tip.z
          : Math.max(previous.z, tip.z)
        : Math.max(this.shallow, tip.z)
    this.shallow = from
    const inBand = tip.z <= TOUCH_IN && tip.z >= -TOUCH_BEHIND
    const pushed = from >= 0 && from - tip.z >= TOUCH_TRAVEL
    if (inBand && pushed && physicalPush) return this.settle(true, tip, [tip.x, tip.y])
    return this.settle(false, tip, undefined)
  }

  /** Let go because the hand it was pressed with has gone away or been reassigned. */
  release(): TapState {
    this.previous = undefined
    this.shallow = undefined
    return this.settle(false, undefined, undefined)
  }

  private within(tip: FaceLocal, slack: number): boolean {
    return (
      Math.abs(tip.x) <= this.face.width / 2 + slack && Math.abs(tip.y) <= this.face.height / 2 + slack
    )
  }

  private settle(
    down: boolean,
    _tip: FaceLocal | undefined,
    at: readonly [number, number] | undefined,
  ): TapState {
    const was = this.down
    this.down = down
    this.at = down ? at : undefined
    if (!down && !was) return IDLE
    return { down, pressedEdge: down && !was, releasedEdge: !down && was, at: this.at }
  }
}

/** World-space pointer intent. The approach direction is frozen while hovering. */
export class PhysicalPoke {
  private start: {x:number;y:number;z:number;nx:number;ny:number;nz:number} | undefined
  reset(): void {this.start=undefined}
  advance(x:number,y:number,z:number,nx:number,ny:number,nz:number,over:boolean):boolean {
    if(!over){this.reset();return false}
    if(this.start===undefined){this.start={x,y,z,nx,ny,nz};return false}
    const a=this.start,travel=(a.x-x)*a.nx+(a.y-y)*a.ny+(a.z-z)*a.nz
    if(travel<0){a.x=x;a.y=y;a.z=z;return false}
    return travel>=TOUCH_TRAVEL
  }
}
